/**
 * Tournament Context - Complete implementation
 * Migrated from TournamentState (models.js)
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import MatchScore from '../models/MatchScore';
import { effectiveCourtCount, maxRoundsWithoutRepetition } from '../utils/helpers';
import { generateBestSchedule } from '../utils/scheduleGenerator';
import DEFAULT_CONFIG from '../config/defaultConfig.json';

const TournamentContext = createContext();

export const TournamentProvider = ({ children }) => {
  // State
  const [config, setConfigState] = useState(DEFAULT_CONFIG);
  const [players, setPlayersState] = useState([]);
  const [courts, setCourtsState] = useState([]);
  const [schedule, setScheduleState] = useState({ rounds: [], hasRepetitions: false, warnings: [] });
  const [currentRound, setCurrentRound] = useState(0);
  const [scheduleGenerated, setScheduleGenerated] = useState(false);
  const [scheduleStats, setScheduleStats] = useState(null);
  const [generationResult, setGenerationResult] = useState(null);
  const [tournamentStarted, setTournamentStarted] = useState(false);
  const [tournamentFinished, setTournamentFinished] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [finalMatch, setFinalMatch] = useState(null);

  /**
   * Calculate number of rounds based on duration
   */
  const calculateRoundCount = useCallback((newConfig) => {
    const totalMatchTime = newConfig.matchDuration + newConfig.pauseDuration;
    const warmupTime = newConfig.warmupDuration || 0;
    const hasFinalPhase = newConfig.hasFinalPhase !== false;
    const finalPhaseTime = hasFinalPhase ? (newConfig.matchDuration || 0) : 0;
    const availableTimeForRounds = newConfig.totalDuration - warmupTime - finalPhaseTime;
    let roundCount = Math.floor(availableTimeForRounds / totalMatchTime);

    // Ensure at least 1 round
    if (roundCount < 1) roundCount = 1;

    return roundCount;
  }, []);

  /**
   * Set tournament configuration
   */
  const setConfig = useCallback((newConfig) => {
    const merged = { ...config, ...newConfig };
    merged.roundCount = calculateRoundCount(merged);
    setConfigState(merged);
    // Only reset scheduleGenerated if no schedule exists
    // This prevents resetting it to false when loading a tournament
    const hasSchedule = schedule && schedule.rounds && schedule.rounds.length > 0;
    if (!hasSchedule) {
      setScheduleGenerated(false);
    }
  }, [config, calculateRoundCount, schedule]);

  /**
   * Set players list
   */
  const setPlayers = useCallback((playersData) => {
    const processedPlayers = playersData.map((data, index) => ({
      id: index,
      name: data.name.trim(),
      position: data.position,
      fixedPartnerId: data.fixedPartnerId !== undefined ? data.fixedPartnerId : null,
      // Stats
      played: 0,
      wins: 0,
      draws: 0,
      losses: 0,
      setsWon: 0,
      setsLost: 0,
      gamesWon: 0,
      gamesLost: 0,
      points: 0
    }));

    const fixedPairsCount = processedPlayers.filter(p => p.fixedPartnerId !== null).length;
    if (fixedPairsCount > 0) {
      console.log(`✅ setPlayers(): ${fixedPairsCount} joueurs avec paires fixes enregistrés`);
    }

    setPlayersState(processedPlayers);
    // Only reset scheduleGenerated if no schedule exists
    // This prevents resetting it to false when loading a tournament
    const hasSchedule = schedule && schedule.rounds && schedule.rounds.length > 0;
    if (!hasSchedule) {
      setScheduleGenerated(false);
    }
  }, [schedule]);

  /**
   * Set courts list
   */
  const setCourts = useCallback((courtsData) => {
    const processedCourts = courtsData.map((data, index) => ({
      id: data.id !== undefined ? data.id : index,
      name: data.name ? data.name.trim() : `Terrain ${index + 1}`
    }));

    setCourtsState(processedCourts);
  }, []);

  /**
   * Get player by ID
   */
  const getPlayer = useCallback((id) => {
    return players.find(p => p.id === id);
  }, [players]);

  /**
   * Get player name by ID
   */
  const getPlayerName = useCallback((id) => {
    const player = getPlayer(id);
    return player ? player.name : `Joueur ${id + 1}`;
  }, [getPlayer]);

  /**
   * Get player position by ID
   */
  const getPlayerPosition = useCallback((id) => {
    const player = getPlayer(id);
    return player ? player.position : 'left';
  }, [getPlayer]);

  /**
   * Get all players by position
   */
  const getPlayersByPosition = useCallback((position) => {
    return players.filter(p => p.position === position);
  }, [players]);

  /**
   * Get configured start time as Date object
   */
  const getConfiguredStartTime = useCallback(() => {
    const timeStr = config.startTimeStr || '09:00';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }, [config.startTimeStr]);

  /**
   * Set generated schedule
   */
  const setSchedule = useCallback((newSchedule) => {
    // Initialize detailedScore for each match
    newSchedule.rounds.forEach(round => {
      round.matches.forEach(match => {
        if (!match.detailedScore) {
          match.detailedScore = new MatchScore(
            config.setsToWin || 1,
            config.gamesPerSet !== undefined ? config.gamesPerSet : null,
            config.tiebreakPoints || 7,
            config.allowDraws !== undefined ? config.allowDraws : true
          );
        }
      });
    });

    setScheduleState(newSchedule);
    setScheduleGenerated(true);
  }, [config]);

  /**
   * Record match result
   */
  const recordResult = useCallback((roundIndex, matchIndex, winner) => {
    setScheduleState(prevSchedule => {
      const newSchedule = { ...prevSchedule };
      const round = newSchedule.rounds[roundIndex];
      if (!round) return prevSchedule;

      const match = round.matches[matchIndex];
      if (!match) return prevSchedule;

      match.result = winner;
      return newSchedule;
    });
  }, []);

  /**
   * Recalculate player stats from all validated matches
   * @param {Object} scheduleOverride - Schedule to use instead of the closure's schedule (avoids stale closure)
   */
  const recalculateRanking = useCallback((scheduleOverride) => {
    const scheduleToUse = scheduleOverride || schedule;

    setPlayersState(prevPlayers => {
      const newPlayers = prevPlayers.map(player => ({
        ...player,
        played: 0,
        wins: 0,
        draws: 0,
        losses: 0,
        setsWon: 0,
        setsLost: 0,
        gamesWon: 0,
        gamesLost: 0,
        points: 0
      }));

      // Recalculate from all validated matches
      if (!scheduleToUse || !scheduleToUse.rounds || !Array.isArray(scheduleToUse.rounds)) {
        return newPlayers;
      }

      scheduleToUse.rounds.forEach(round => {
        round.matches.forEach(match => {
          if (!match.validated || match.result === null) return;

          const isDraw = match.result === 0;
          const team1Wins = match.result === 1;
          const team2Wins = match.result === 2;

          if (match.playerResults) {
            // Individual player results (new format)
            Object.entries(match.playerResults).forEach(([playerIdStr, stats]) => {
              const playerId = parseInt(playerIdStr);
              const player = newPlayers.find(p => p.id === playerId);
              if (player) {
                player.played++;
                const isTeam1Player = match.team1.includes(playerId);
                if (isDraw) player.draws++;
                else if ((isTeam1Player && team1Wins) || (!isTeam1Player && team2Wins)) player.wins++;
                else player.losses++;
                player.setsWon += stats.setsWon;
                player.setsLost += stats.setsLost;
                player.gamesWon += stats.gamesWon;
                player.gamesLost += stats.gamesLost;
                player.points = player.gamesWon - player.gamesLost;
              }
            });
          } else {
            // Fallback: team stats (backward compatibility with old tournaments)
            const detailedScore = match.detailedScore;
            match.team1.forEach(playerId => {
              const player = newPlayers.find(p => p.id === playerId);
              if (player) {
                player.played++;
                if (isDraw) player.draws++;
                else if (team1Wins) player.wins++;
                else player.losses++;
                if (detailedScore) {
                  player.setsWon += detailedScore.stats.team1.setsWon;
                  player.setsLost += detailedScore.stats.team1.setsLost;
                  player.gamesWon += detailedScore.stats.team1.gamesWon;
                  player.gamesLost += detailedScore.stats.team1.gamesLost;
                }
                player.points = player.gamesWon - player.gamesLost;
              }
            });
            match.team2.forEach(playerId => {
              const player = newPlayers.find(p => p.id === playerId);
              if (player) {
                player.played++;
                if (isDraw) player.draws++;
                else if (team2Wins) player.wins++;
                else player.losses++;
                if (detailedScore) {
                  player.setsWon += detailedScore.stats.team2.setsWon;
                  player.setsLost += detailedScore.stats.team2.setsLost;
                  player.gamesWon += detailedScore.stats.team2.gamesWon;
                  player.gamesLost += detailedScore.stats.team2.gamesLost;
                }
                player.points = player.gamesWon - player.gamesLost;
              }
            });
          }
        });
      });

      return newPlayers;
    });
  }, [schedule]);

  /**
   * Get ranking sorted by performance
   */
  const getRanking = useCallback(() => {
    const ranking = [...players];
    ranking.sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      if (b.setsWon !== a.setsWon) return b.setsWon - a.setsWon;
      if (b.gamesWon !== a.gamesWon) return b.gamesWon - a.gamesWon;
      if (a.gamesLost !== b.gamesLost) return a.gamesLost - b.gamesLost;
      return a.name.localeCompare(b.name);
    });
    return ranking;
  }, [players]);

  /**
   * Get current round data
   */
  const getCurrentRound = useCallback(() => {
    if (!schedule || !schedule.rounds || !Array.isArray(schedule.rounds)) {
      return null;
    }
    return schedule.rounds[currentRound] || null;
  }, [schedule, currentRound]);

  /**
   * Check if all results for current round are entered
   */
  const isCurrentRoundComplete = useCallback(() => {
    const round = getCurrentRound();
    if (!round) return false;
    return round.matches.every(match => match.result !== null);
  }, [getCurrentRound]);

  /**
   * Advance to next round
   */
  const advanceRound = useCallback(() => {
    if (!schedule || !schedule.rounds || !Array.isArray(schedule.rounds)) {
      return false;
    }
    if (currentRound < schedule.rounds.length - 1) {
      setCurrentRound(prev => prev + 1);
      return true;
    }
    return false;
  }, [currentRound, schedule]);

  /**
   * Get configuration summary
   */
  const getConfigSummary = useCallback(() => {
    const { roundCount, matchDuration, pauseDuration } = config;
    const totalTime = roundCount * (matchDuration + pauseDuration) - pauseDuration;
    return `${roundCount} tours de ${matchDuration} min + ${pauseDuration} min pause = ${totalTime} min total`;
  }, [config]);

  /**
   * Get configuration warnings
   */
  const getConfigWarnings = useCallback(() => {
    const warnings = [];
    const { playerCount, courtCount, roundCount } = config;

    const effectiveCourts = effectiveCourtCount(playerCount, courtCount);
    const maxRoundsNoRepeat = maxRoundsWithoutRepetition(playerCount);

    if (effectiveCourts < courtCount) {
      warnings.push(`Pas assez de joueurs pour ${courtCount} terrains. ${effectiveCourts} terrain(s) seront utilises.`);
    }

    if (playerCount % 4 !== 0) {
      const restingCount = playerCount % 4;
      warnings.push(`${restingCount} joueur(s) au repos a chaque tour.`);
    }

    if (roundCount > maxRoundsNoRepeat) {
      const toursAvecRepetition = roundCount - maxRoundsNoRepeat;
      warnings.push(`${maxRoundsNoRepeat} tour(s) sans repetition, puis ${toursAvecRepetition} tour(s) avec repetitions possibles.`);
    }

    return warnings;
  }, [config]);

  /**
   * Reset tournament state
   */
  const reset = useCallback(() => {
    setConfigState(DEFAULT_CONFIG);
    setPlayersState([]);
    setScheduleState({ rounds: [], hasRepetitions: false, warnings: [] });
    setCurrentRound(0);
    setScheduleGenerated(false);
    setTournamentStarted(false);
    setTournamentFinished(false);
    setStartTime(null);
    setFinalMatch(null);
  }, []);

  /**
   * Export state for saving
   */
  const exportState = useCallback(() => {
    let scheduleForExport = schedule;

    if (schedule && schedule.rounds && Array.isArray(schedule.rounds)) {
      scheduleForExport = {
        ...schedule,
        rounds: schedule.rounds.map(round => ({
          ...round,
          matches: round.matches ? round.matches.map(match => ({
            ...match,
            detailedScore: match.detailedScore ? match.detailedScore.toJSON() : null
          })) : []
        }))
      };
    }

    // Determine if schedule is actually generated (has rounds with matches)
    const hasGeneratedSchedule = schedule &&
                                   schedule.rounds &&
                                   Array.isArray(schedule.rounds) &&
                                   schedule.rounds.length > 0;

    // Strip detailedScore from finalMatch (in-memory only)
    let finalMatchForExport = null;
    if (finalMatch) {
      const { detailedScore, ...finalMatchWithoutScore } = finalMatch;
      finalMatchForExport = finalMatchWithoutScore;
    }

    return {
      config,
      players,
      courts,
      schedule: scheduleForExport || {},
      currentRound,
      scheduleGenerated: hasGeneratedSchedule ? true : scheduleGenerated,
      tournamentStarted,
      tournamentFinished,
      startTime,
      finalMatch: finalMatchForExport
    };
  }, [config, players, courts, schedule, currentRound, scheduleGenerated, tournamentStarted, tournamentFinished, startTime, finalMatch]);

  /**
   * Import saved state
   */
  const importState = useCallback((state) => {
    if (state.config) setConfigState(state.config);

    if (state.players) {
      const playersArray = Array.isArray(state.players) ? state.players : Object.values(state.players);

      // Ensure all players have stats properties
      playersArray.forEach(player => {
        if (typeof player.played === 'undefined') {
          player.played = 0;
          player.wins = 0;
          player.draws = 0;
          player.losses = 0;
          player.setsWon = 0;
          player.setsLost = 0;
          player.gamesWon = 0;
          player.gamesLost = 0;
          player.points = 0;
        }
      });

      setPlayersState(playersArray);
    }

    if (state.courts) {
      const courtsArray = Array.isArray(state.courts) ? state.courts : [];
      setCourtsState(courtsArray);
    }

    let loadedSchedule = null;

    if (state.schedule) {
      const newSchedule = state.schedule;

      // Deserialize detailedScore for each match
      if (newSchedule.rounds) {
        newSchedule.rounds.forEach(round => {
          if (round.matches) {
            round.matches.forEach(match => {
              const ms = new MatchScore(
                state.config.setsToWin || 1,
                state.config.gamesPerSet !== undefined ? state.config.gamesPerSet : null,
                state.config.tiebreakPoints || 7,
                state.config.allowDraws !== undefined ? state.config.allowDraws : true
              );

              if (match.playerResults && match.team1 && match.team1.length > 0) {
                const p1Stats = match.playerResults[match.team1[0]];
                if (p1Stats) {
                  ms.stats.team1 = {
                    gamesWon: p1Stats.gamesWon,
                    gamesLost: p1Stats.gamesLost,
                    setsWon: p1Stats.setsWon,
                    setsLost: p1Stats.setsLost
                  };
                  ms.stats.team2 = {
                    gamesWon: p1Stats.gamesLost,
                    gamesLost: p1Stats.gamesWon,
                    setsWon: p1Stats.setsLost,
                    setsLost: p1Stats.setsWon
                  };
                  ms.sets = [{ team1: p1Stats.gamesWon, team2: p1Stats.gamesLost, tiebreak: null }];
                }
                if (match.result === 1) ms.winner = 0;
                else if (match.result === 2) ms.winner = 1;
                else if (match.result === 0) ms.winner = 'draw';
              } else if (match.detailedScore?.sets?.length > 0) {
                // Restore in-progress score from saved sets (non-validated match)
                match.detailedScore.sets.forEach((set, i) => {
                  ms.setSetScore(i, set.team1, set.team2, set.tiebreak || null);
                });
                ms.calculate();
              }

              match.detailedScore = ms;
            });
          }
        });
      }

      setScheduleState(newSchedule);
      loadedSchedule = newSchedule;
    }

    if (typeof state.currentRound === 'number') setCurrentRound(state.currentRound);
    if (typeof state.scheduleGenerated === 'boolean') setScheduleGenerated(state.scheduleGenerated);
    if (typeof state.tournamentStarted === 'boolean') setTournamentStarted(state.tournamentStarted);
    if (typeof state.tournamentFinished === 'boolean') setTournamentFinished(state.tournamentFinished);
    if (state.startTime) setStartTime(new Date(state.startTime));
    else if ('startTime' in state) setStartTime(null);

    // Restore finalMatch with reconstructed detailedScore
    if (!state.finalMatch && 'finalMatch' in state) {
      setFinalMatch(null);
    } else if (state.finalMatch) {
      const fm = state.finalMatch;
      const ms = new MatchScore(
        state.config?.setsToWin || 1,
        state.config?.gamesPerSet !== undefined ? state.config.gamesPerSet : null,
        state.config?.tiebreakPoints || 7,
        state.config?.allowDraws !== undefined ? state.config.allowDraws : true
      );
      if (fm.validated && fm.playerResults && fm.team1 && fm.team1.length > 0) {
        const p1Stats = fm.playerResults[fm.team1[0]];
        if (p1Stats) {
          ms.stats.team1 = { gamesWon: p1Stats.gamesWon, gamesLost: p1Stats.gamesLost, setsWon: p1Stats.setsWon, setsLost: p1Stats.setsLost };
          ms.stats.team2 = { gamesWon: p1Stats.gamesLost, gamesLost: p1Stats.gamesWon, setsWon: p1Stats.setsLost, setsLost: p1Stats.setsWon };
          ms.sets = [{ team1: p1Stats.gamesWon, team2: p1Stats.gamesLost, tiebreak: null }];
        }
        if (fm.result === 1) ms.winner = 0;
        else if (fm.result === 2) ms.winner = 1;
        else if (fm.result === 0) ms.winner = 'draw';
      }
      setFinalMatch({ ...fm, detailedScore: ms });
    }

    // Recalculate ranking from loaded schedule so it's correct on page refresh
    if (loadedSchedule) {
      recalculateRanking(loadedSchedule);
    }
  }, [recalculateRanking]);

  /**
   * Generate tournament schedule with progress callback
   * @param {Function} onProgress - Progress callback (current, total, bestScore)
   * @param {number} candidateCount - Number of candidates to generate (default 500)
   * @returns {Promise<Object>} Generation result
   */
  const generateSchedule = useCallback(async (onProgress = null, candidateCount = 500) => {
    if (!players || players.length === 0) {
      throw new Error('No players configured');
    }

    if (!config) {
      throw new Error('No configuration');
    }

    // Use pre-calculated roundCount from config (already accounts for hasFinalPhase)
    const roundCount = config.roundCount;

    if (!roundCount || roundCount < 1) {
      throw new Error('Not enough time for at least one round');
    }

    // Generate best schedule
    const result = await generateBestSchedule(
      players,
      config.courtCount,
      roundCount,
      config.matchDuration,
      config.pauseDuration,
      candidateCount,
      onProgress
    );

    // Update state
    setScheduleState(result.schedule);
    setScheduleStats(result.stats);
    setGenerationResult(result);
    setScheduleGenerated(true);

    return result;
  }, [players, config]);

  const value = {
    // State
    config,
    players,
    courts,
    schedule,
    currentRound,
    scheduleGenerated,
    scheduleStats,
    generationResult,
    tournamentStarted,
    tournamentFinished,
    startTime,

    // Actions
    setConfig,
    setPlayers,
    setCourts,
    setSchedule,
    generateSchedule,
    recordResult,
    recalculateRanking,
    reset,
    exportState,
    importState,

    // Setters
    setCurrentRound,
    setScheduleGenerated,
    setTournamentStarted,
    setTournamentFinished,
    setStartTime,
    finalMatch,
    setFinalMatch,

    // Getters
    getPlayer,
    getPlayerName,
    getPlayerPosition,
    getPlayersByPosition,
    getConfiguredStartTime,
    getRanking,
    getCurrentRound,
    isCurrentRoundComplete,
    advanceRound,
    getConfigSummary,
    getConfigWarnings
  };

  return (
    <TournamentContext.Provider value={value}>
      {children}
    </TournamentContext.Provider>
  );
};

export const useTournament = () => {
  const context = useContext(TournamentContext);
  if (!context) {
    throw new Error('useTournament must be used within TournamentProvider');
  }
  return context;
};

export default TournamentContext;
